import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Loader2, Settings, Mail, Save } from "lucide-react";
import { toast } from "sonner";

import api, { formatApiError } from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

export default function ConfiguracionSistema() {
  const [correos, setCorreos] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/configuracion");
      setCorreos(data.correos_alertas_facturacion || "");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const guardar = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/configuracion", { correos_alertas_facturacion: correos });
      setCorreos(data.correos_alertas_facturacion || "");
      toast.success("Configuración guardada.");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <DashboardLayout>
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} data-testid="config-sistema-page">
        <div className="mb-8">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 flex items-center gap-2"><Settings className="h-4 w-4" /> Plataforma</p>
          <h1 className="font-display text-4xl font-black tracking-tight">Configuración del Sistema</h1>
          <p className="text-sm text-[#52525B] mt-2 max-w-xl">Parámetros globales de la plataforma.</p>
        </div>

        {loading ? (
          <div className="p-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : (
          <div className="max-w-2xl border border-[#e4e4e7] bg-white">
            <div className="flex items-center gap-3 px-6 py-5 border-b border-[#e4e4e7]">
              <div className="h-10 w-10 bg-[#0A0A0A] flex items-center justify-center"><Mail className="h-5 w-5 text-[#FFCC00]" strokeWidth={2.2} /></div>
              <div>
                <p className="font-display font-black text-lg leading-none tracking-tight">Alertas de Facturación</p>
                <p className="text-[10px] tracking-[0.2em] text-[#52525B] font-bold mt-1 uppercase">Equipo de contabilidad</p>
              </div>
            </div>
            <div className="px-6 py-6 space-y-3">
              <Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">Correos para recepción de alertas de facturación (separados por comas)</Label>
              <Textarea
                value={correos}
                onChange={(e) => setCorreos(e.target.value)}
                placeholder="contabilidad@empresa.com, facturas@empresa.com"
                data-testid="correos-facturacion-input"
                className="rounded-none border-[#0A0A0A] min-h-[90px] font-mono text-sm"
              />
              <p className="text-xs text-[#52525B]">Cuando un tenant solicite factura, se enviará la alerta a estos correos. Si se deja vacío, la alerta llega al SuperAdmin principal.</p>
              <button onClick={guardar} disabled={saving} data-testid="config-sistema-save" className="btn-hard h-11 px-5 bg-[#FFCC00] text-[#0A0A0A] border border-[#0A0A0A] font-bold uppercase tracking-wider text-sm inline-flex items-center gap-2 disabled:opacity-60">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Guardar
              </button>
            </div>
          </div>
        )}
      </motion.div>
    </DashboardLayout>
  );
}
